export const CHECKOUT_URL = "https://reflexology-en.impultienda.ar/checkout/";

export const images = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/9Ywapgovtxy2o9JQNnQaUm/REFLEXOLOGY-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/v67GaoQBBhWUEZi6yxnL8F/ELLY_DELGADO_-_REFLEXOLOGY.png",
};

export const scatteredItems = [
  "A foot chart here.",
  "A hand chart there.",
  "Notes from a course.",
  "A saved video.",
  "A PDF you downloaded months ago.",
];

export const practiceQuestions = [
  "Where do I start?",
  "Which area do I reference?",
  "What comes next?",
  "How should I organize the session?",
];

export const systemFlow = ["Find it", "Understand it", "Organize it", "Practice it"];

export const beforeItems = [
  "You search through multiple resources before practicing.",
  "You have different charts but don't know which one to reference.",
  "You constantly go back to previous lessons to remember basic concepts.",
  "You know the theory but struggle to organize it into a practical sequence.",
  "You spend more time searching than practicing.",
  "You feel like you have plenty of information… but not enough structure.",
];

export const afterItems = [
  "Your key foot and hand maps are organized in one system.",
  "You have visual references designed for quick consultation.",
  "You can follow structured practice sequences.",
  "You have protocols to guide your practice.",
  "You have checklists to keep your process organized.",
  "You can access your materials from your phone, tablet, or computer.",
];

export type PainIcon = "chart" | "search" | "unsure" | "clock" | "files" | "repeat";

export const painPoints: { icon: PainIcon; text: string }[] = [
  { icon: "chart", text: "You have a reflexology chart, but you're still unsure how to turn all those zones into a practical sequence." },
  { icon: "search", text: "You spend too much time searching through notes, videos, or PDFs before you begin." },
  { icon: "unsure", text: "You understand the basics but still feel unsure about what to study or practice next." },
  { icon: "clock", text: "You want to practice more consistently, but organizing everything feels overwhelming." },
  { icon: "files", text: "You've collected several reflexology resources, but they're scattered across different files and platforms." },
  { icon: "repeat", text: "Every time you want to review something, you feel like you need to go back and study everything again." },
];

export type Volume = {
  number: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro?: string;
  flow?: string[];
  flowLabel?: string;
  cta?: string;
};

export const volumes: Volume[] = [
  {
    number: "I",
    title: "The Reflexology Master Map",
    tagline: "Build the foundation before you start practicing.",
    intro: "I'll walk you through the basic framework you need to understand how the REFLEXOLOGY 360° system works.",
    items: ["How to use the system", "Essential reflexology concepts", "Zone organization", "How to read the maps", "Quick-reference guidance", "Key terminology", "Study framework", "Beginner reference guide"],
    outro: "This volume gives you the big picture before you dive into the individual maps.",
  },
  {
    number: "II",
    title: "The Visual Foot Reflexology Atlas",
    tagline: "Your visual reference guide for the feet.",
    intro: "Instead of searching through different charts, you'll have your foot reference system organized in one place.",
    items: ["Detailed foot maps", "Zone references", "Visual labels", "Location guides", "Quick-reference pages", "Study sheets", "Organized visual charts"],
    outro: "No more trying to remember everything from memory. Open the map. Find the area. Continue your practice.",
    cta: "I want the Foot Reflexology Atlas",
  },
  {
    number: "III",
    title: "The Visual Hand Reflexology Atlas",
    tagline: "Because reflexology isn't limited to the feet.",
    intro: "This volume gives you a visual reference system for studying and reviewing key reflexology areas of the hands.",
    items: ["Hand maps", "Zone references", "Visual labels", "Location guides", "Comparison charts", "Quick-reference sheets", "Study materials"],
    outro: "Everything is organized to make visual review easier.",
  },
  {
    number: "IV",
    title: "The Step-by-Step Session System",
    tagline: "Turn information into a practical sequence.",
    intro: "One of the biggest problems beginners face isn't finding information. It's knowing what to do with it. That's why I created a simple session framework:",
    flow: ["Prepare", "Position", "Warm up", "Apply", "Relax", "Close"],
    flowLabel: "The session framework",
    items: ["Session preparation", "Space organization", "General sequence", "Suggested timing", "Practice structure", "Session checklist", "Closing sequence", "Organization tips"],
    outro: "So you have a framework to follow instead of improvising every step.",
    cta: "Show me the Session System",
  },
  {
    number: "V",
    title: "The Reflexology Wellness Protocol Library",
    tagline: "Stop wondering what sequence to practice next.",
    intro: "Inside this volume, I've organized structured educational protocols focused on relaxation and general wellness. Each protocol follows the same easy-to-reference structure:",
    items: ["Objective", "Preparation", "Suggested duration", "Sequence", "Application order", "Practice notes", "Closing", "Checklist"],
    outro: "This makes it easier to take what you've learned and organize it into a practical session structure.",
    cta: "I want the Protocol Library",
  },
  {
    number: "VI",
    title: "The Pocket Reflexology Practice Guide",
    tagline: "Take your entire reference system with you.",
    intro: "This volume is designed as your quick-reference companion.",
    items: ["Session worksheets", "Practice logs", "Pre-session checklists", "Post-session checklists", "Progress trackers", "Personal notes", "Quick-reference pages", "Practice organization tools", "Mobile-friendly resources"],
    outro: "Whether you're at home, studying, or preparing for practice — your reference system is always with you.",
    cta: "Yes! I want all 6 volumes",
  },
];

export type ToolIcon = "map" | "checklist" | "compass" | "notes" | "phone";

export const tools: { icon: ToolIcon; title: string; text: string }[] = [
  { icon: "map", title: "Visual Reflexology Maps", text: "Organized foot and hand maps designed for easier study and quick reference." },
  { icon: "checklist", title: "Practice Checklists", text: "Simple checklists to help you follow a consistent structure." },
  { icon: "compass", title: "Step-by-Step Sequences", text: "Visual frameworks that help you understand what comes before and after each stage." },
  { icon: "notes", title: "Practice Worksheets", text: "Tools to record your practice, observations, questions, and progress." },
  { icon: "phone", title: "Quick-Reference Guides", text: "Compact resources you can access from your phone whenever you need a fast reminder." },
];

export type Bonus = {
  number: number;
  title: string;
  value: number;
  image: string;
  intro: string;
  listLabel: string;
  items: string[];
  flow?: string[];
  outro: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "50 Reflexology Study Cards",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/mepa4AAdFr2SUvDcN2zakb/BONUS_1_REFLEXOLOGY-EN.png",
    intro: "A collection of visual study cards to help you review key reflexology concepts and references.",
    listLabel: "Use them to",
    items: ["Review", "Study", "Quiz yourself", "Practice", "Reinforce concepts", "Study from your phone", "Print for offline use"],
    outro: "Instead of sitting down for another long study session… you can review one card at a time.",
  },
  {
    number: 2,
    title: "The 15-Minute Practice Protocol™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/7oWdmyqQPaqbYJGhuUzAoC/BONUS_2_REFLEXOLOGY-EN.png",
    intro: "A compact framework designed to help you organize a basic reflexology practice session within a simple 15-minute structure.",
    listLabel: "You'll get",
    items: ["Preparation", "Opening sequence", "Practice sequence", "Closing sequence", "Timing guide", "Checklist"],
    flow: ["Open", "Follow", "Check off"],
    outro: "No complicated planning. No jumping between resources. Just one structured reference.",
  },
  {
    number: 3,
    title: "The Beginner's Reflexology Mistakes Guide",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/jUG4eY6nkqPDGP3xDVdrMt/BONUS_3_REFLEXOLOGY-EN.png",
    intro: "Common mistakes beginners can make while studying and practicing reflexology — collected in one guide.",
    listLabel: "Identify potential issues involving",
    items: ["Organization", "Sequence", "Map reference", "Preparation", "Practice habits", "Documentation", "Consistency"],
    outro: "Because sometimes improving isn't about learning more. It's about stopping the habits that make learning harder.",
  },
  {
    number: 4,
    title: "The 21-Day Reflexology Practice Planner",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/h6QFSPUEfePM5c4E3vQZWP/BONUS_4_REFLEXOLOGY-EN.png",
    intro: "A simple 21-day framework designed to help you turn reflexology study into a consistent practice routine.",
    listLabel: "You'll also receive",
    items: ["Daily tracker", "Practice checklist", "Notes section", "Weekly review", "Progress tracker", "Final self-assessment"],
    flow: ["Learn", "Practice", "Record", "Progress"],
    outro: "Every day follows four simple steps — so you always know what to do next.",
  },
  {
    number: 5,
    title: "30 Reflexology Practice Checklists",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/non7qGaHzUHzg2Lw2A5Gbe/BONUS_5_REFLEXOLOGY-EN.png",
    intro: "A collection of practical checklists for every step of your practice.",
    listLabel: "Covering",
    items: ["Preparation", "Study", "Practice", "Session organization", "Materials", "Recording", "Follow-up", "Review"],
    outro: "Print them. Save them on your phone. Keep them beside your practice materials.",
  },
];

export const guaranteeItems = [
  "Explore all 6 volumes",
  "Review the visual maps",
  "Open the practice protocols",
  "Use the worksheets",
  "Test the checklists",
  "Explore all 5 bonuses",
];

export const testimonials = [
  {
    quote: "I had reflexology information scattered across different resources. Having everything organized in one place made studying much easier.",
    name: "[Real Customer Name]",
    country: "[Country]",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/908e29c6-f280-440a-8aa7-40802e81ff59.jpg",
  },
  {
    quote: "The visual maps are my favorite part. I can quickly find what I'm looking for without going through an entire course.",
    name: "[Real Customer Name]",
    country: "[Country]",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b05d3d28-f505-4cb4-9759-e99035ddddc8.jpg",
  },
  {
    quote: "The 21-day planner gave me the structure I needed to actually practice consistently.",
    name: "[Real Customer Name]",
    country: "[Country]",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/6759b11c-4fd8-4890-b52f-5eccf29ad58e.jpg",
  },
  {
    quote: "It doesn't feel like another PDF. It feels like an actual reference system.",
    name: "[Real Customer Name]",
    country: "[Country]",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/5f18ec5d-fc64-4619-b93e-e3e30358e91e.jpg",
  },
];

export const creatorFlow = ["Visualize", "Understand", "Organize", "Practice", "Record"];

export const faqs = [
  { q: "How will I receive REFLEXOLOGY 360°™?", a: "After completing your purchase, you'll receive instructions to access and download the 6 volumes and 5 bonuses in digital format." },
  { q: "Can I use it on my phone?", a: "Yes. The system is designed to be accessed from your phone, tablet, or computer." },
  { q: "Do I need previous reflexology experience?", a: "No. You don't need previous experience to use the material as an educational and study resource. The content is organized progressively, from fundamentals through visual references, protocols, worksheets, and practice tools." },
  { q: "Is this a professional certification?", a: "No. REFLEXOLOGY 360°™ is an educational and practical reference system. It is not a professional license, medical qualification, or substitute for formal professional training. If you intend to practice professionally, you should check the requirements and regulations applicable in your location." },
  { q: "Are the foot and hand maps included?", a: "Yes. The visual foot and hand maps are included in the main volumes." },
  { q: "Are the protocols medical?", a: "No. The protocols are educational reflexology sequences focused on relaxation and general wellness. They are not intended to diagnose, prevent, or treat medical conditions." },
  { q: "Is REFLEXOLOGY 360°™ a video course?", a: "No. It is primarily a digital visual reference and practice system containing volumes, maps, protocols, worksheets, checklists, and study resources." },
  { q: "Is it a one-time payment?", a: "Yes. You pay $27 once. There are no monthly subscription fees." },
  { q: "Do I get lifetime access?", a: "Yes, while the product remains available." },
  { q: "Can I print the materials?", a: "Yes. You can print the appropriate maps, worksheets, checklists, and other print-friendly resources for personal use." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. If you decide the product isn't right for you, you can request a refund according to the purchase terms." },
];

export const finalIncluded = [
  "6 Premium Volumes",
  "5 Exclusive Bonuses",
  "Visual Foot & Hand Maps",
  "Practice Protocols",
  "Checklists",
  "Practice Worksheets",
  "Quick-Reference Guides",
];

export const recentBuyers = [
  { name: "María", place: "Mexico City, Mexico" },
  { name: "Sofía", place: "Madrid, Spain" },
  { name: "Valentina", place: "Bogotá, Colombia" },
  { name: "Carmen", place: "Miami, USA" },
  { name: "Lucía", place: "Buenos Aires, Argentina" },
  { name: "Isabel", place: "Lima, Peru" },
  { name: "Gabriela", place: "Santiago, Chile" },
  { name: "Rosa", place: "Los Angeles, USA" },
  { name: "Daniela", place: "Quito, Ecuador" },
  { name: "Ana", place: "San Juan, Puerto Rico" },
  { name: "Patricia", place: "Montevideo, Uruguay" },
  { name: "Elena", place: "Toronto, Canada" },
  { name: "Camila", place: "Santo Domingo, Dominican Republic" },
  { name: "Andrea", place: "San José, Costa Rica" },
  { name: "Mariana", place: "London, United Kingdom" },
];
